import React, {useEffect} from 'react';
import {StyleSheet, Platform, ScrollView} from 'react-native';
import {useForm, Controller} from 'react-hook-form';
import moment from 'moment';
import TextInput from '../../components/TextInput';
import Error from '../../components/Error';
import Button from '../../components/Button';
import SelectInput from '../../components/SelectInput';
import DateTimePickerWeb from '../../components/DatePickerWeb';
import {getSelect, isNotDefaultSelect, isPositiveNumber} from '../../utils';
import {isCasParticulier, isEnergieEtPuissance, isOccasion} from './condition';
import {
  vehiculesData,
  typeVehiculeData,
  typeEnergieVehiculeData,
  casParticulierActivitesData,
} from './data';
import {calculTMC, getMontantTaxeEnvironnement} from './calcul';

const now = moment().format('YYYY-MM-DD');
const start = moment().subtract(50, 'years').format('YYYY-MM-DD');

const FormTMCWeb = ({setTERV, setTMC, setTotal}) => {
  const {register, handleSubmit, setValue, control, errors, watch} = useForm({
    mode: 'onChange',
    defaultValues: {
      genreVehicule: vehiculesData[0],
      typeVehicule: 'default',
      typeEnergie: 'default',
      puissanceVehicule: 0,
      valeurVehicule: 0,
      dateMiseEnCirculation: now,
      casParticulierActivite: 'default',
    },
  });

  const onSubmit = (values) => {
    console.log(values);
    const terv = getMontantTaxeEnvironnement(values.genreVehicule);
    const tmc = calculTMC(values);
    console.log('terv : ' + terv);
    console.log('TMC : ' + tmc);
    const total = tmc + terv;
    setTERV(terv);
    setTMC(tmc);
    setTotal(total);
  };

  const valeurVehicule = watch('valeurVehicule');
  const genreVehicule = watch('genreVehicule');
  const typeVehicule = watch('typeVehicule');

  useEffect(() => {
    register('genreVehicule', {
      required: true,
      validate: (value) => isNotDefaultSelect(value),
    });
    register('typeVehicule', {
      required: true,
      validate: (value) => isNotDefaultSelect(value),
    });
    register('typeEnergie', {
      required: true,
      validate: (value) => isNotDefaultSelect(value),
    });
    register('puissanceVehicule', {
      validate: (value) => isPositiveNumber(value),
    });
    register('valeurVehicule', {
      required: true,
      validate: (value) => isPositiveNumber(value),
    });
    register('dateMiseEnCirculation');
    register('casParticulierActivite');
  }, [register]);

  return (
    <ScrollView>
      <Controller
        control={control}
        name="genreVehicule"
        render={(props) => (
          <SelectInput
            noDefaultValue={true}
            selectedValue={props.value}
            onValueChange={props.onChange}
            label="Quel est le genre de votre véhicule ?"
            data={getSelect(vehiculesData)}
          />
        )}
      />

      {errors.genreVehicule?.type === 'required' && (
        <Error>Ce champ est obligatoire !</Error>
      )}

      {errors.genreVehicule?.type === 'validate' && (
        <Error>Veuillez choisir une valeur !</Error>
      )}

      <Controller
        control={control}
        name="typeVehicule"
        render={(props) => (
          <SelectInput
            selectedValue={props.value}
            onValueChange={props.onChange}
            label="Votre véhicule est il un véhicule d'occasion ou neuf ?"
            data={getSelect(typeVehiculeData)}
          />
        )}
      />

      {errors.typeVehicule?.type === 'required' && (
        <Error>Ce champ est obligatoire !</Error>
      )}

      {errors.typeVehicule?.type === 'validate' && (
        <Error>Veuillez choisir une valeur !</Error>
      )}

      {isOccasion(typeVehicule) && (
        <>
          <Controller
            control={control}
            name="dateMiseEnCirculation"
            render={(props) => (
              <DateTimePickerWeb
                onDateChange={props.onChange}
                date={props.value}
                minDate={start}
                maxDate={now}
                label="Si votre véhicule est d'occasion, indiquer la date de 1ère mise en circulation :"
              />
            )}
            rules={{required: true}}
          />

          {errors.dateMiseEnCirculation && (
            <Error>Ce champ est obligatoire !</Error>
          )}
        </>
      )}

      {isEnergieEtPuissance(genreVehicule) && (
        <>
          <Controller
            control={control}
            name="typeEnergie"
            render={(props) => (
              <SelectInput
                selectedValue={props.value}
                onValueChange={props.onChange}
                label="Le type d'énergie du véhicule"
                data={getSelect(typeEnergieVehiculeData)}
              />
            )}
            rules={{
              required: true,
              validate: (value) => isNotDefaultSelect(value),
            }}
          />
          {errors.typeEnergie?.type === 'required' && (
            <Error>Ce champ est obligatoire !</Error>
          )}

          {errors.typeEnergie?.type === 'validate' && (
            <Error>Veuillez choisir une valeur !</Error>
          )}
        </>
      )}

      {isEnergieEtPuissance(genreVehicule) && (
        <>
          <Controller
            control={control}
            name="puissanceVehicule"
            render={(props) => (
              <TextInput
                onChangeText={props.onChange}
                value={props.value}
                label="La puissance du véhicule en CV"
                placeholder="Veuillez entrer une valeur"
                keyboardType={Platform.OS === 'ios' ? 'number-pad' : 'numeric'}
              />
            )}
            rules={{
              required: true,
              validate: (value) => isPositiveNumber(value),
            }}
          />

          {errors.puissanceVehicule?.type === 'required' && (
            <Error>Ce champ est obligatoire !</Error>
          )}

          {errors.puissanceVehicule?.type === 'validate' && (
            <Error>Ce champ doit être un entier positif !</Error>
          )}
        </>
      )}

      <Controller
        control={control}
        name="valeurVehicule"
        render={(props) => (
          <TextInput
            onChangeText={props.onChange}
            value={props.value}
            label={`Quelle est la valeur du véhicule en XPF" : ${valeurVehicule}`}
            placeholder="Veuillez entrer une valeur"
            keyboardType={Platform.OS === 'ios' ? 'number-pad' : 'numeric'}
          />
        )}
      />

      {errors.valeurVehicule?.type === 'required' && (
        <Error>Ce champ est obligatoire !</Error>
      )}

      {errors.valeurVehicule?.type === 'validate' && (
        <Error>Ce champ doit être un entier positif !</Error>
      )}

      {isCasParticulier(genreVehicule) && (
        <>
          <Controller
            control={control}
            name="casParticulierActivite"
            render={(props) => (
              <SelectInput
                selectedValue={props.value}
                onValueChange={props.onChange}
                label="Etes vous concerné par l'un de ces cas particuliers ?"
                data={getSelect(casParticulierActivitesData)}
              />
            )}
          />
        </>
      )}

      <Button onPress={handleSubmit(onSubmit)} label="Faire la simulation" />
    </ScrollView>
  );
};

const styles = StyleSheet.create({});

export default FormTMCWeb;
